/**
 * The browser transport. `fetch` with a `ReadableStream` reader behind the
 * same interface the conformance runner implements with `node:http`.
 *
 * Not `EventSource`, deliberately: the chat request is a POST with a JSON
 * body and the resume carries a custom `Last-Event-ID` header, and
 * `EventSource` supports neither. See docs/DECISIONS.md entry 10.
 */

import { TransportHttpError, type StreamRequest, type StreamTransport } from '@harness/stream-core';

export function createFetchTransport(): StreamTransport {
  return {
    open(request: StreamRequest): AsyncIterable<Uint8Array> {
      return openStream(request);
    },
  };
}

async function* openStream(request: StreamRequest): AsyncGenerator<Uint8Array> {
  // Bridge the package owned cancel signal to the platform one.
  const controller = new AbortController();
  const detach = request.signal.onAbort(() => {
    controller.abort();
  });

  try {
    const headers: Record<string, string> = { accept: 'text/event-stream' };
    if (request.body !== undefined) headers['content-type'] = 'application/json';
    if (request.lastEventId !== undefined) headers['last-event-id'] = request.lastEventId;

    const response = await fetch(request.url, {
      method: request.body === undefined ? 'GET' : 'POST',
      headers,
      ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
      signal: controller.signal,
      cache: 'no-store',
    });

    if (!response.ok) {
      const rawRetryAfter = response.headers.get('retry-after');
      const retryAfterSeconds =
        rawRetryAfter !== null && /^\d+$/.test(rawRetryAfter)
          ? Number.parseInt(rawRetryAfter, 10)
          : undefined;
      await response.body?.cancel();
      throw new TransportHttpError(response.status, {
        ...(retryAfterSeconds === undefined ? {} : { retryAfterSeconds }),
      });
    }

    const body = response.body;
    if (body === null) throw new Error('The response carried no body to read.');

    const reader = body.getReader();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) return;
        yield value;
      }
    } finally {
      // Releases the connection when the machine stops reading, whether by
      // cancel, watchdog, or terminal frame.
      await reader.cancel().catch(() => undefined);
    }
  } finally {
    detach();
  }
}
